/**
 * Skafe Lines Carousel Component
 * Fully compatible with Shopify OS 2.0 Visual Theme Editor:
 * - Responds to shopify:block:select / shopify:block:deselect / shopify:section:select
 * - Automatically detects block additions, removals, and reordering via MutationObserver
 * - Touch swiping, mouse drag, chevron arrow navigation, and live pagination dots
 */
if (!customElements.get('skafe-lines-carousel')) {
  customElements.define(
    'skafe-lines-carousel',
    class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.viewport = this.querySelector('[data-viewport]');
        this.prevBtn = this.querySelector('[data-arrow-prev]');
        this.nextBtn = this.querySelector('[data-arrow-next]');
        this.dotsContainer = this.querySelector('[data-dots]');
        this.updateSlides();

        if (!this.track || this.slides.length === 0) return;

        this.currentIndex = 0;
        this.ticking = false;
        this.drag = null;

        this.initDots();
        this.bindEvents();
        this.bindThemeEditorEvents();
        this.updateState();

        if (this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.isInThemeEditorBlockSelection) {
          this.startAutoplay();
        }

        // ResizeObserver to recalculate pagination and states on viewport changes / editor drawer toggle
        this.resizeObserver = new ResizeObserver(() => {
          this.initDots();
          this.updateState();
        });
        this.resizeObserver.observe(this.track);

        // MutationObserver to detect blocks added/removed/reordered in Shopify Theme Editor
        this.mutationObserver = new MutationObserver(() => {
          this.updateSlides();
          this.initDots();
          this.updateState();
        });
        this.mutationObserver.observe(this.track, { childList: true, subtree: false });
      }

      disconnectedCallback() {
        this.stopAutoplay();
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
        if (this.mutationObserver) {
          this.mutationObserver.disconnect();
        }
        this.unbindThemeEditorEvents();
      }

      updateSlides() {
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      }

      getVisibleSlideCount() {
        if (!this.track || this.slides.length === 0) return 1;
        const trackWidth = this.track.clientWidth;
        const firstSlide = this.slides[0];
        const slideWidth = firstSlide ? firstSlide.offsetWidth : 1;
        return Math.max(1, Math.round(trackWidth / Math.max(slideWidth, 1)));
      }

      getPageCount() {
        const visible = this.getVisibleSlideCount();
        return Math.max(1, Math.ceil(this.slides.length / visible));
      }

      initDots() {
        if (!this.dotsContainer) return;
        const pageCount = this.getPageCount();

        if (pageCount <= 1) {
          this.dotsContainer.innerHTML = '';
          this.dotsContainer.style.display = 'none';
          return;
        }

        this.dotsContainer.style.display = 'flex';
        let html = '';
        for (let i = 0; i < pageCount; i++) {
          html += `<button type="button" class="skafe-lines-carousel__dot${i === this.currentIndex ? ' is-active' : ''}" data-index="${i}" aria-label="Ir para página ${i + 1}" role="tab" aria-selected="${i === this.currentIndex ? 'true' : 'false'}"></button>`;
        }
        this.dotsContainer.innerHTML = html;

        this.dots = Array.from(this.dotsContainer.querySelectorAll('.skafe-lines-carousel__dot'));
        this.dots.forEach((dot) => {
          dot.addEventListener('click', (e) => {
            const idx = parseInt(e.currentTarget.dataset.index, 10);
            this.scrollToPage(idx);
          });
        });
      }

      bindEvents() {
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            this.updateState();
          });
        };
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', () => {
            this.stopAutoplay();
            this.scrollByPages(-1);
          });
        }

        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => {
            this.stopAutoplay();
            this.scrollByPages(1);
          });
        }

        // Pointer drag interactions
        this.track.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => {
          if (this.dataset.autoplay === 'true' && !this.isInThemeEditorBlockSelection) {
            this.startAutoplay();
          }
        });
      }

      bindThemeEditorEvents() {
        this.onBlockSelectHandler = (event) => {
          const slide = event.target.classList?.contains('skafe-lines-carousel__slide')
            ? event.target
            : event.target.closest?.('.skafe-lines-carousel__slide');

          if (slide && this.contains(slide)) {
            this.isInThemeEditorBlockSelection = true;
            this.stopAutoplay();
            this.scrollToSlide(slide);
          }
        };

        this.onBlockDeselectHandler = (event) => {
          const slide = event.target.classList?.contains('skafe-lines-carousel__slide')
            ? event.target
            : event.target.closest?.('.skafe-lines-carousel__slide');

          if (slide && this.contains(slide)) {
            this.isInThemeEditorBlockSelection = false;
            if (this.dataset.autoplay === 'true') {
              this.startAutoplay();
            }
          }
        };

        this.onSectionSelectHandler = (event) => {
          if (event.target === this || event.target.contains(this)) {
            this.updateSlides();
            this.initDots();
            this.updateState();
          }
        };

        document.addEventListener('shopify:block:select', this.onBlockSelectHandler);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselectHandler);
        document.addEventListener('shopify:section:select', this.onSectionSelectHandler);
      }

      unbindThemeEditorEvents() {
        if (this.onBlockSelectHandler) {
          document.removeEventListener('shopify:block:select', this.onBlockSelectHandler);
        }
        if (this.onBlockDeselectHandler) {
          document.removeEventListener('shopify:block:deselect', this.onBlockDeselectHandler);
        }
        if (this.onSectionSelectHandler) {
          document.removeEventListener('shopify:section:select', this.onSectionSelectHandler);
        }
      }

      onPointerDown(e) {
        if (e.pointerType === 'touch') return;
        if (e.button !== 0) return;

        this.drag = {
          startX: e.clientX,
          scrollStart: this.track.scrollLeft,
          moved: false,
        };

        this.onPointerMove = (moveEvent) => {
          if (!this.drag) return;
          const diff = moveEvent.clientX - this.drag.startX;
          if (Math.abs(diff) > 5) {
            this.drag.moved = true;
            this.classList.add('is-dragging');
          }
          this.track.scrollLeft = this.drag.scrollStart - diff;
        };

        this.onPointerUp = () => {
          if (this.drag && this.drag.moved) {
            setTimeout(() => this.classList.remove('is-dragging'), 50);
          } else {
            this.classList.remove('is-dragging');
          }
          this.drag = null;
          window.removeEventListener('pointermove', this.onPointerMove);
          window.removeEventListener('pointerup', this.onPointerUp);
        };

        window.addEventListener('pointermove', this.onPointerMove);
        window.addEventListener('pointerup', this.onPointerUp);
      }

      scrollByPages(direction) {
        const visible = this.getVisibleSlideCount();
        const firstSlide = this.slides[0];
        const slideWidth = (firstSlide ? firstSlide.offsetWidth : 200) + 16;
        const scrollDistance = slideWidth * Math.max(1, visible - 1) * direction;
        this.track.scrollBy({ left: scrollDistance, behavior: 'smooth' });
      }

      scrollToPage(pageIndex) {
        const visible = this.getVisibleSlideCount();
        const targetSlideIndex = Math.min(pageIndex * visible, this.slides.length - 1);
        const targetSlide = this.slides[targetSlideIndex];
        if (targetSlide) {
          this.track.scrollTo({ left: targetSlide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
        }
      }

      scrollToSlide(target) {
        let slide = target;
        if (typeof target === 'number') {
          slide = this.slides[target];
        }
        if (!slide || !this.track) return;

        const trackRect = this.track.getBoundingClientRect();
        const slideRect = slide.getBoundingClientRect();
        const isPartiallyVisible = slideRect.left >= trackRect.left && slideRect.right <= trackRect.right;

        if (!isPartiallyVisible) {
          this.track.scrollTo({
            left: slide.offsetLeft - this.track.offsetLeft,
            behavior: 'smooth',
          });
        }
      }

      updateState() {
        if (!this.track) return;
        const scrollLeft = this.track.scrollLeft;
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);

        // Update arrow disabled states
        if (this.prevBtn) {
          this.prevBtn.disabled = scrollLeft <= 4;
        }
        if (this.nextBtn) {
          this.nextBtn.disabled = scrollLeft >= maxScroll - 4;
        }

        // Update dots state
        const pageCount = this.getPageCount();
        if (pageCount > 1 && this.dots && this.dots.length > 0) {
          let activePage = 0;
          if (maxScroll > 0) {
            const progress = Math.max(0, Math.min(1, scrollLeft / maxScroll));
            activePage = Math.min(pageCount - 1, Math.round(progress * (pageCount - 1)));
          }
          this.currentIndex = activePage;
          this.dots.forEach((dot, i) => {
            const isActive = i === activePage;
            dot.classList.toggle('is-active', isActive);
            dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
          });
        }
      }

      startAutoplay() {
        this.stopAutoplay();
        const speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
        this.autoplayTimer = setInterval(() => {
          const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
          if (this.track.scrollLeft >= maxScroll - 10) {
            this.track.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            this.scrollByPages(1);
          }
        }, speed);
      }

      stopAutoplay() {
        if (this.autoplayTimer) {
          clearInterval(this.autoplayTimer);
          this.autoplayTimer = null;
        }
      }
    }
  );
}
